import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

export interface RecentNumber {
  to_number: string;
  last_called_at: string;
  last_customer_name: string | null;
  last_brand: string | null;
}

interface CallRow {
  to_number: string | null;
  started_at: string;
  brand: string | null;
  conversation_id: string | null;
  conversations?: { customer_name: string | null } | null;
}

export function useRecentNumbers(limit = 10): { numbers: RecentNumber[]; isLoading: boolean } {
  const [numbers, setNumbers] = useState<RecentNumber[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setIsLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        if (!cancelled) setIsLoading(false);
        return;
      }
      const since = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();
      const { data } = await supabase
        .from('calls')
        .select('to_number, started_at, brand, conversation_id, conversations:conversation_id(customer_name)')
        .eq('answered_by', user.id)
        .eq('direction', 'outbound')
        .gte('started_at', since)
        .order('started_at', { ascending: false })
        .limit(50);

      const seen = new Set<string>();
      const uniq: RecentNumber[] = [];
      for (const row of (data ?? []) as unknown as CallRow[]) {
        if (!row.to_number || seen.has(row.to_number)) continue;
        seen.add(row.to_number);
        uniq.push({
          to_number: row.to_number,
          last_called_at: row.started_at,
          last_customer_name: row.conversations?.customer_name ?? null,
          last_brand: row.brand ?? null,
        });
        if (uniq.length >= limit) break;
      }
      if (!cancelled) {
        setNumbers(uniq);
        setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [limit]);

  return { numbers, isLoading };
}
